import React, { useEffect, useState, useMemo } from "react";
import { useParams, useSearchParams, useNavigate, Link } from "react-router-dom";
import { api } from "../lib/api";
import { isNativeApp, payWithStripeInApp } from "../lib/nativePayment";
import { useAuth } from "../context/AuthContext";
import { useSettings } from "../context/SettingsContext";
import { getPublicPath } from "../utils/seoPages";
import { useSetHelpMessage } from "../context/HelpContext";
import Spinner from "../components/Spinner";
import RichTextRenderer from "../components/RichTextRenderer";
import { CreditCard, Bitcoin, ShieldCheck, AlertCircle, Tag, Sparkles, CheckCircle2, X, Package, Wallet } from "lucide-react";
import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";

export default function Checkout() {
  // Routes:
  //   /checkout/:courseId   (course)
  //   /checkout/pack/:packId (pack)
  const { courseId, packId } = useParams();
  const isPack = !!packId;

  const [params] = useSearchParams();
  const ref = params.get("ref") || localStorage.getItem("sf_ref") || "";
  const navigate = useNavigate();
  const { user } = useAuth();
  const { settings } = useSettings();
  const cgvPath = getPublicPath(settings, "/conditions-generales-de-vente");
  const privacyPath = getPublicPath(settings, "/politique-confidentialite");

  const [course, setCourse] = useState(null);
  const [pack, setPack] = useState(null);
  const [upsellCourse, setUpsellCourse] = useState(null);
  const [includeUpsell, setIncludeUpsell] = useState(false);

  const [promoCode, setPromoCode] = useState("");
  const [appliedPromo, setAppliedPromo] = useState(null);
  const [promoError, setPromoError] = useState("");
  const [validatingPromo, setValidatingPromo] = useState(false);

  const [cart, setCart] = useState(null);
  const [loading, setLoading] = useState(true);

  const [method, setMethod] = useState("card");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [paypalConfig, setPaypalConfig] = useState({ configured: false });
  const [affiliateWallet, setAffiliateWallet] = useState({ available_eur: 0 });
  const [termsAccepted, setTermsAccepted] = useState(false);

  // Initial load
  useEffect(() => {
    (async () => {
      try {
        if (isPack) {
          const r = await api.get(`/packs/${packId}`);
          setPack(r.data);
        } else {
          const r = await api.get(`/courses/${courseId}`);
          setCourse(r.data);
          if (r.data?.upsell_course_id) {
            try {
              const up = await api.get(`/courses/${r.data.upsell_course_id}`);
              setUpsellCourse(up.data);
            } catch {}
          }
        }
      } finally {
        setLoading(false);
      }
      api.get("/payments/paypal/config").then((r) => setPaypalConfig(r.data)).catch(() => {});
      api.get("/affiliate/me").then((r) => setAffiliateWallet(r.data?.wallet || { available_eur: 0 })).catch(() => {});
    })();
  }, [courseId, packId, isPack]);

  // Recompute cart whenever choice changes
  const cartBody = useMemo(() => ({
    course_id: isPack ? null : courseId,
    pack_id: isPack ? packId : null,
    extra_course_ids: includeUpsell && upsellCourse ? [upsellCourse.course_id] : [],
    promo_code: appliedPromo?.code || null,
  }), [courseId, packId, isPack, includeUpsell, upsellCourse, appliedPromo]);

  const product = isPack ? pack : course;
  useSetHelpMessage(
    product ? `Bonjour ! J'ai besoin d'aide pour finaliser ma commande "${product.title}" (${(cart?.total ?? product.price_eur).toFixed(2)}€).` : null,
    [product?.title, cart?.total, product?.price_eur]
  );

  useEffect(() => {
    if (!course && !pack) return;
    api.post("/cart/preview", cartBody)
      .then((r) => setCart(r.data))
      .catch((e) => setError(e.response?.data?.detail || "Erreur de calcul du panier"));
  }, [cartBody, course, pack]);

  if (loading) return <Spinner className="min-h-[60vh]" />;
  if (!user) { navigate(`/login?next=/checkout/${isPack ? "pack/" + packId : courseId}`); return null; }
  if (!course && !pack) return <p className="text-center mt-16">Introuvable.</p>;

  const rawTotal = Number(cart?.total ?? (isPack ? pack.price_eur : course.price_eur));
  const isFreeCheckout = Number.isFinite(rawTotal) && rawTotal <= 0.01;
  const total = isFreeCheckout ? 0 : rawTotal;
  const baseAmount = Number(cart?.base_amount ?? total);
  const discount = Number(cart?.discount_eur || 0);

  const validatePromo = async () => {
    setPromoError(""); setValidatingPromo(true);
    try {
      const { data } = await api.post("/promo/validate", {
        code: promoCode.trim().toUpperCase(),
        course_id: isPack ? null : courseId,
        pack_id: isPack ? packId : null,
        extra_course_ids: includeUpsell && upsellCourse ? [upsellCourse.course_id] : [],
      });
      if (data.valid) {
        setAppliedPromo({ code: data.code, ...data });
        setPromoCode("");
      } else {
        setPromoError(data.message || "Code invalide");
      }
    } catch (e) {
      setPromoError(e.response?.data?.detail || "Erreur");
    } finally {
      setValidatingPromo(false);
    }
  };

  const removePromo = () => { setAppliedPromo(null); setPromoError(""); };

  const ensureCheckoutTermsAccepted = () => {
    if (!termsAccepted) {
      setError("Tu dois accepter le contrat et les conditions générales de vente avant de payer.");
      return false;
    }
    return true;
  };

  const startStripe = async (m = "card") => {
    // Sécurité UX : si le panier est offert, on ne doit jamais ouvrir Stripe.
    if (isFreeCheckout) {
      await claimFreeOrder();
      return;
    }
    if (!ensureCheckoutTermsAccepted()) return;
    setBusy(true); setError("");
    try {
      const body = {
        course_id: isPack ? null : courseId,
        pack_id: isPack ? packId : null,
        extra_course_ids: includeUpsell && upsellCourse ? [upsellCourse.course_id] : [],
        promo_code: appliedPromo?.code || null,
        origin_url: window.location.origin,
        referral_code: ref || null,
        method: m,
        terms_accepted: termsAccepted,
      };
      const { data } = await api.post("/payments/stripe/create-session", body);

      // Dans l'app : on paie dans le navigateur intégré pour ne jamais sortir de
      // l'application, et on revient automatiquement une fois le paiement validé.
      if (isNativeApp() && data.session_id) {
        const outcome = await payWithStripeInApp(data.url, data.session_id);
        if (outcome === "paid") {
          navigate(`/checkout/success?session_id=${encodeURIComponent(data.session_id)}`);
          return;
        }
        if (outcome === "expired") setError("La session de paiement a expiré. Relance le paiement.");
        setBusy(false);
        return;
      }

      window.location.href = data.url;
    } catch (e) {
      setError(e.response?.data?.detail || "Erreur de paiement");
      setBusy(false);
    }
  };

  const claimFreeOrder = async () => {
    if (!ensureCheckoutTermsAccepted()) return;
    setBusy(true); setError("");
    try {
      await api.post("/payments/free/claim", {
        course_id: isPack ? null : courseId,
        pack_id: isPack ? packId : null,
        extra_course_ids: includeUpsell && upsellCourse ? [upsellCourse.course_id] : [],
        promo_code: appliedPromo?.code || null,
        referral_code: ref || null,
        terms_accepted: termsAccepted,
      });

      // Commande offerte : aucune session Stripe/PayPal n'existe.
      // On évite donc la page success basée sur session_id/course_id pour ne pas
      // déclencher de 404, puis on envoie directement l'apprenant vers son espace.
      navigate("/dashboard", { replace: true });
    } catch (e) {
      setError(e.response?.data?.detail || "Impossible de valider cette formation offerte");
      setBusy(false);
    }
  };

  const startAffiliateWallet = async () => {
    if (isFreeCheckout) {
      await claimFreeOrder();
      return;
    }
    if (!ensureCheckoutTermsAccepted()) return;
    setBusy(true); setError("");
    try {
      const { data } = await api.post("/payments/affiliate-wallet/pay", {
        course_id: isPack ? null : courseId,
        pack_id: isPack ? packId : null,
        extra_course_ids: includeUpsell && upsellCourse ? [upsellCourse.course_id] : [],
        promo_code: appliedPromo?.code || null,
        referral_code: ref || null,
        terms_accepted: termsAccepted,
      });
      navigate(`/checkout/success?course_id=${data.course_id || data.pack_id || (isPack ? packId : courseId)}`);
    } catch (e) {
      setError(e.response?.data?.detail || "Solde parrainage insuffisant ou erreur de paiement");
      setBusy(false);
    }
  };

  // Prix du bonus : on prend celui calcule par le serveur des que le bonus est
  // dans le panier. Il peut etre inferieur au simple "-X%" quand la commande a
  // ete alignee sur le prix du pack equivalent (une formation + son bonus ne
  // doivent jamais couter plus cher que le pack contenant les memes formations).
  const upsellListPrice = upsellCourse ? Number(upsellCourse.price_eur) : 0;
  const upsellPrice = !upsellCourse
    ? 0
    : includeUpsell && cart?.upsell_price_eur != null
      ? Number(cart.upsell_price_eur)
      : upsellListPrice * (1 - (course?.upsell_discount_pct || 0) / 100);
  const upsellDiscountPct =
    upsellListPrice > 0 ? Math.round((1 - upsellPrice / upsellListPrice) * 100) : 0;

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12" data-testid="checkout-page">
      <h1 className="font-heading text-3xl sm:text-4xl font-black mb-2">Finaliser ta commande</h1>
      <p className="text-gray-600 mb-8">{isPack ? <>Tu vas acheter le pack <strong>{pack.title}</strong>.</> : <>Tu vas rejoindre <strong>{course.title}</strong>.</>}</p>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-4">
          {/* Upsell offer (only for single course with upsell defined) */}
          {!isPack && upsellCourse && (
            <div className={`card p-5 transition-all relative ${includeUpsell ? "ring-2 ring-brand-orange bg-amber-50/30" : ""}`} data-testid="upsell-card">
              <div className="flex items-start gap-3">
                <div className="w-12 h-12 rounded-xl bg-amber-100 flex items-center justify-center flex-shrink-0">
                  <Sparkles className="w-6 h-6 text-brand-orange" />
                </div>
                <div className="flex-1">
                  <p className="badge orange mb-2">Offre Bonus exclusive</p>
                  <p className="font-heading font-bold mb-1">Ajoute aussi <span className="text-brand-orange">{upsellCourse.title}</span> à -{upsellDiscountPct}%</p>
                  <RichTextRenderer html={upsellCourse.short_description} className="text-sm text-gray-600 mb-3" />
                  <div className="flex items-center gap-3 mb-3">
                    <span className="text-gray-400 line-through text-sm">{upsellCourse.price_eur}€</span>
                    <span className="font-heading text-xl font-bold text-brand-orange">{upsellPrice.toFixed(2)}€</span>
                    <span className="text-sm text-gray-500">au lieu de {upsellCourse.price_eur}€</span>
                  </div>
                  <button
                    onClick={() => setIncludeUpsell(!includeUpsell)}
                    className={includeUpsell ? "btn-orange" : "btn-ghost"}
                    data-testid="upsell-toggle-btn"
                  >
                    {includeUpsell ? <><CheckCircle2 className="w-4 h-4" /> Bonus ajouté — Retirer</> : <>+ Ajouter à ma commande</>}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Promo code */}
          <div className="card p-5" data-testid="promo-section">
            <p className="font-heading font-bold mb-2 flex items-center gap-2"><Tag className="w-4 h-4 text-brand-green" /> Code promo</p>
            {appliedPromo ? (
              <div className="flex items-center justify-between p-3 bg-emerald-50 border border-emerald-200 rounded-lg">
                <div>
                  <p className="font-mono font-bold text-brand-green">{appliedPromo.code}</p>
                  <p className="text-xs text-gray-600">−{appliedPromo.discount_eur.toFixed(2)}€ {appliedPromo.description ? `· ${appliedPromo.description}` : ""}</p>
                </div>
                <button onClick={removePromo} className="text-red-500 hover:text-red-700" data-testid="promo-remove-btn"><X className="w-4 h-4" /></button>
              </div>
            ) : (
              <div className="flex gap-2">
                <input
                  className="input flex-1 uppercase"
                  placeholder="Entre ton code (ex: BIENVENUE10)"
                  value={promoCode}
                  onChange={(e) => setPromoCode(e.target.value.toUpperCase())}
                  data-testid="promo-input"
                />
                <button onClick={validatePromo} disabled={!promoCode.trim() || validatingPromo} className="btn-primary" data-testid="promo-apply-btn">
                  {validatingPromo ? "..." : "Appliquer"}
                </button>
              </div>
            )}
            {promoError && <p className="text-sm text-red-600 mt-2" data-testid="promo-error">{promoError}</p>}
          </div>

          {/* Payment methods */}
          {isFreeCheckout ? (
            <div className="card p-5 border-2 border-emerald-200 bg-emerald-50" data-testid="free-checkout-notice">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-xl bg-white flex items-center justify-center">
                  <CheckCircle2 className="w-6 h-6 text-brand-green" />
                </div>
                <div>
                  <p className="font-heading font-bold text-brand-green">Formation offerte</p>
                  <p className="text-sm text-gray-600">Ce code promo couvre 100% de la commande : aucun paiement Stripe, PayPal ou CB ne sera demandé.</p>
                </div>
              </div>
            </div>
          ) : (
            <>
          <button onClick={() => setMethod("card")} className={`w-full text-left card p-5 transition-all ${method === "card" ? "ring-2 ring-brand-green" : "hover:bg-emerald-50"}`} data-testid="pay-method-card">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-emerald-100 flex items-center justify-center"><CreditCard className="w-6 h-6 text-brand-green" /></div>
              <div>
                <p className="font-heading font-bold">Carte bancaire (Stripe)</p>
                <p className="text-sm text-gray-600">Visa, Mastercard, Amex — paiement sécurisé via Stripe</p>
              </div>
            </div>
          </button>

          <button onClick={() => setMethod("stripe-crypto")} className={`w-full text-left card p-5 transition-all ${method === "stripe-crypto" ? "ring-2 ring-brand-green" : "hover:bg-emerald-50"}`} data-testid="pay-method-stripe-crypto">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-amber-100 flex items-center justify-center"><Bitcoin className="w-6 h-6 text-brand-orange" /></div>
              <div>
                <p className="font-heading font-bold">Crypto (USDC) <span className="badge orange ml-2">via Stripe</span></p>
                <p className="text-sm text-gray-600">Paie en stablecoins (USDC) directement via Stripe — sans wallet à connecter.</p>
              </div>
            </div>
          </button>

          <button onClick={() => setMethod("affiliate-wallet")} className={`w-full text-left card p-5 transition-all ${method === "affiliate-wallet" ? "ring-2 ring-brand-green" : "hover:bg-emerald-50"}`} data-testid="pay-method-affiliate-wallet">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-emerald-100 flex items-center justify-center"><Wallet className="w-6 h-6 text-brand-green" /></div>
              <div>
                <p className="font-heading font-bold">Portefeuille parrainage <span className="badge ml-2">Solde {Number(affiliateWallet.available_eur || 0).toFixed(2)}€</span></p>
                <p className="text-sm text-gray-600">Utilise tes commissions disponibles pour acheter cette formation.</p>
              </div>
            </div>
          </button>

          <button onClick={() => setMethod("paypal")} className={`w-full text-left card p-5 transition-all ${method === "paypal" ? "ring-2 ring-brand-green" : "hover:bg-emerald-50"}`} data-testid="pay-method-paypal">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center text-2xl">🅿️</div>
              <div>
                <p className="font-heading font-bold">PayPal {!paypalConfig.configured && <span className="badge gray ml-2">Bientôt</span>}{paypalConfig.mode === "sandbox" && paypalConfig.configured && <span className="badge orange ml-2">Sandbox</span>}</p>
                <p className="text-sm text-gray-600">Paiement via ton compte PayPal ou ta carte (sans compte requis).</p>
              </div>
            </div>
          </button>
            </>
          )}


          <div className={`card p-5 transition-all ${termsAccepted ? "ring-1 ring-emerald-200 bg-emerald-50/30" : ""}`} data-testid="terms-acceptance-section">
            <label className="flex cursor-pointer items-start gap-3">
              <input
                type="checkbox"
                checked={termsAccepted}
                onChange={(e) => { setTermsAccepted(e.target.checked); if (e.target.checked) setError(""); }}
                className="mt-1 h-5 w-5 rounded border-emerald-200 text-brand-green focus:ring-brand-green"
                data-testid="terms-acceptance-checkbox"
              />
              <span className="text-sm text-gray-700">
                J’ai lu et j’accepte le <Link to={cgvPath} target="_blank" className="font-semibold text-brand-green hover:underline">contrat et les conditions générales de vente</Link> ainsi que la <Link to={privacyPath} target="_blank" className="font-semibold text-brand-green hover:underline">politique de confidentialité</Link>.
              </span>
            </label>
          </div>

          {error && <div className="p-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm flex items-start gap-2" data-testid="checkout-error"><AlertCircle className="w-4 h-4 mt-0.5" /> {error}</div>}

          <div className="card p-6">
            {isFreeCheckout ? (
              <button
                onClick={claimFreeOrder}
                disabled={busy || !termsAccepted || !appliedPromo}
                className="btn-primary w-full justify-center"
                data-testid="checkout-free-btn"
              >
                {busy ? "Validation de l'accès..." : "Valider ma formation offerte"}
              </button>
            ) : (
              <>
                {method === "card" && (
                  <button onClick={() => startStripe("card")} disabled={busy || !termsAccepted} className="btn-primary w-full justify-center" data-testid="checkout-card-btn">
                    {busy ? "Redirection vers Stripe..." : `Payer ${total.toFixed(2)}€ avec ma carte`}
                  </button>
                )}
                {method === "stripe-crypto" && (
                  <button onClick={() => startStripe("crypto")} disabled={busy || !termsAccepted} className="btn-primary w-full justify-center" data-testid="checkout-stripe-crypto-btn">
                    {busy ? "Redirection..." : `Payer en USDC via Stripe (${total.toFixed(2)} USD)`}
                  </button>
                )}
                {method === "affiliate-wallet" && (
                  <button
                    onClick={startAffiliateWallet}
                    disabled={busy || !termsAccepted || Number(affiliateWallet.available_eur || 0) < total}
                    className="btn-primary w-full justify-center"
                    data-testid="checkout-affiliate-wallet-btn"
                  >
                    {busy
                      ? "Validation du paiement..."
                      : Number(affiliateWallet.available_eur || 0) >= total
                        ? `Payer ${total.toFixed(2)}€ avec mon portefeuille`
                        : `Solde insuffisant (${Number(affiliateWallet.available_eur || 0).toFixed(2)}€ disponible)`}
                  </button>
                )}
                {method === "paypal" && (
              <div className="text-center" data-testid="paypal-section">
                {paypalConfig.configured && termsAccepted ? (
                  <PayPalScriptProvider
                    options={{
                      clientId: paypalConfig.client_id,
                      currency: "EUR",
                      intent: "capture",
                    }}
                  >
                    <PayPalButtons
                      key={`${total}-${appliedPromo?.code || "no-promo"}-${includeUpsell}`}
                      style={{ layout: "vertical", color: "gold", shape: "pill", label: "paypal" }}
                      createOrder={async () => {
                        if (isFreeCheckout) {
                          await claimFreeOrder();
                          throw new Error("Commande offerte : PayPal non requis.");
                        }
                        try {
                          const { data } = await api.post("/payments/paypal/create-order", {
                            course_id: isPack ? null : courseId,
                            pack_id: isPack ? packId : null,
                            extra_course_ids: includeUpsell && upsellCourse ? [upsellCourse.course_id] : [],
                            promo_code: appliedPromo?.code || null,
                            referral_code: ref || null,
                            terms_accepted: termsAccepted,
                          });
                          return data.id;
                        } catch (e) {
                          setError(e.response?.data?.detail || "Erreur PayPal");
                          throw e;
                        }
                      }}
                      onApprove={async (data) => {
                        try {
                          await api.post(`/payments/paypal/capture-order/${data.orderID}`);
                          navigate(`/checkout/success?course_id=${isPack ? packId : courseId}`);
                        } catch (e) {
                          setError(e.response?.data?.detail || "La capture du paiement a échoué");
                        }
                      }}
                      onError={(err) => {
                        console.error("PayPal error", err);
                        setError("Erreur PayPal — réessaie ou choisis un autre moyen.");
                      }}
                      onCancel={() => setError("Paiement PayPal annulé.")}
                    />
                  </PayPalScriptProvider>
                ) : (
                  <p className="text-sm text-gray-600 py-6">{paypalConfig.configured ? "Accepte d’abord les conditions générales de vente pour payer avec PayPal." : "PayPal n'est pas encore configuré sur cette plateforme."}</p>
                )}
              </div>
                )}
              </>
            )}
          </div>
        </div>

        <aside className="card p-6 self-start">
          {product.cover_image && <img src={product.cover_image} alt="" className="w-full aspect-video object-cover rounded-lg mb-4" />}
          <p className="font-heading font-bold text-lg flex items-center gap-2">
            {isPack && <Package className="w-4 h-4 text-brand-orange" />}
            {product.title}
          </p>
          <RichTextRenderer html={product.short_description} className="text-xs text-gray-500 mt-1" />
          <hr className="my-4" />

          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span>{isPack ? "Pack" : "Formation"}</span>
              <span className="font-semibold">{product.price_eur.toFixed(2)} €</span>
            </div>
            {includeUpsell && upsellCourse && (
              <div className="flex justify-between text-brand-orange">
                <span>+ Bonus: {upsellCourse.title.slice(0, 28)}…</span>
                <span className="font-semibold">{upsellPrice.toFixed(2)} €</span>
              </div>
            )}
            {discount > 0 && (
              <div className="flex justify-between text-emerald-600">
                <span>Code {appliedPromo?.code}</span>
                <span className="font-semibold">−{discount.toFixed(2)} €</span>
              </div>
            )}
            {ref && (
              <div className="flex justify-between text-amber-700">
                <span>Recommandé par</span>
                <span className="font-semibold">{ref}</span>
              </div>
            )}
          </div>

          <hr className="my-4" />
          <div className="flex justify-between font-heading font-bold text-lg">
            <span>Total</span>
            <span className="text-brand-green" data-testid="checkout-total">{total.toFixed(2)} €</span>
          </div>
          <p className="text-xs text-gray-500 mt-4 flex items-center gap-1"><ShieldCheck className="w-3.5 h-3.5" /> Paiement sécurisé · Accès à vie</p>
        </aside>
      </div>
    </div>
  );
}
